import {
  Box,
  ButtonGroup,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { FormProvider } from "react-hook-form";

import { UserAutocompleteSelect } from "@/common/components/Autocomplete/UserAutocompleteSelect";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import ModalCancelButton from "@/common/components/ModalCancelButton";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import useApiForm from "@/common/hooks/useApiForm";
import { useSlugParam } from "@/common/hooks/useParam";
import api from "@/common/utils/api";
import { UserOnlineStatus } from "@/feature/components/UserOnlineStatus";
import { useUserFeedbackPromptingStatus } from "@/feature/data/useUserFeedbackPromptingStatus";
import {
  SendPromptArgs,
  SendPromptArgsSchema,
  SendPromptDTO,
} from "@reflag/shared/featureFeedbackAPI";
import { z } from "@reflag/shared/utils/zod";

type PromptFormType = SendPromptArgs;

type FeedbackPromptModalProps = {
  isOpen: boolean;
  onClose: () => void;
  question?: string;
};

export function FeedbackPromptModal({
  isOpen,
  onClose,
  question,
}: FeedbackPromptModalProps) {
  const { featureId } = useSlugParam("feature");
  const { envId, appId } = useEnvironment();
  const toast = useToast();

  const { form, handleSubmit } = useApiForm<PromptFormType, SendPromptDTO>(
    async (data) => {
      const res =
        await api.post<"/apps/:appId/features/:featureId/feedback/prompt">(
          `/apps/${appId}/features/${featureId}/feedback/prompt`,
          data,
          { params: { envId: envId! } },
        );
      return res.data;
    },
    SendPromptArgsSchema as z.ZodType<PromptFormType, PromptFormType>,
    {
      onSuccess: () => {
        toast({
          title: "Test prompt sent",
          status: "success",
        });
      },
    },
    {
      mode: "onChange",
      reValidateMode: "onChange",
      defaultValues: { question },
    },
  );

  useEffect(() => {
    form.setValue("question", question);
  }, [form, question]);

  const userId = form.watch("userId");
  const userStatus = useUserFeedbackPromptingStatus(userId);

  return (
    <Modal isOpen={isOpen} size="md" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading size="lg">Try automated survey in-app</Heading>
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit}>
            <FormProvider {...form}>
              <VStack align="flex-start" maxW="compactForm" spacing={4}>
                <Box color="gray.500">
                  Here you can test the automated survey integration directly in
                  your app by triggering a prompt for a given user.
                </Box>

                <ManagedFormControl
                  label="User"
                  name="userId"
                  render={({ field }) => (
                    <UserAutocompleteSelect
                      {...field}
                      placeholder="Select a user"
                      valueAsId
                    />
                  )}
                />

                <UserOnlineStatus userStatus={userStatus.data!} />

                <FormRootError />
                <FormSubmit loadingText="Triggering..." showPostSubmit>
                  Trigger prompt
                </FormSubmit>
              </VStack>
            </FormProvider>
          </form>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <ModalCancelButton shouldNavigate={false} onClick={onClose} />
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
